import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, SignOut } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

/**
 * Global session-expired popup. Shown automatically when the inactivity timer
 * fires (10 min on mobile / 30 min on desktop). All copy is in English.
 */
export const SessionExpiredModal = () => {
  const { sessionExpired, dismissSessionExpired } = useAuth();
  const navigate = useNavigate();

  if (!sessionExpired) return null;

  const isMobile = typeof window !== 'undefined' && window.location.pathname.startsWith('/m/');

  const handleSignIn = () => {
    dismissSessionExpired();
    navigate(isMobile ? '/m/login' : '/login', { replace: true });
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="session-expired-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}
      >
        <motion.div
          initial={{ scale: 0.92, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.92, y: 20 }}
          transition={{ type: 'spring', damping: 22, stiffness: 260 }}
          style={{
            width: '100%', maxWidth: '380px',
            background: '#0f1116',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px',
            padding: '28px 22px 22px',
            boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
            textAlign: 'center',
            color: 'white',
            fontFamily: 'Manrope, system-ui, sans-serif',
          }}
        >
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            margin: '0 auto 16px',
            background: 'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(239,68,68,0.04))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Clock size={30} weight="duotone" color="#ef4444" />
          </div>
          <h2 style={{
            fontFamily: 'Outfit, sans-serif', fontSize: '20px', fontWeight: 700,
            marginBottom: '8px',
          }}>
            Logged out due to inactivity
          </h2>
          <p style={{
            color: 'rgba(255,255,255,0.65)', fontSize: '13px', lineHeight: 1.6,
            marginBottom: '20px',
          }}>
            For your security, your session was ended after {isMobile ? '10' : '30'} minutes
            without activity. Please sign in again to continue.
          </p>
          <button
            data-testid="session-expired-signin-btn"
            onClick={handleSignIn}
            style={{
              width: '100%', padding: '13px 16px',
              background: '#2563eb', color: 'white',
              border: 'none', borderRadius: '12px',
              fontSize: '14px', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            <SignOut size={16} weight="bold" /> Sign in again
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SessionExpiredModal;
